import { Fragment, useLayoutEffect, useRef, useState } from "react";
import DonIcon from "@/assets/icons/don.svg?react";
import { Stage } from "../tasks.types";
import { cn, formatNumber } from "@/shared/utils";

// Minimal gap kept between two award labels before we treat them as collided.
const LABEL_GAP = 6;

/**
 * Fill percentage of the stage bar.
 *
 * Stages get one equal segment each instead of being spread by their raw
 * `value`: real tasks use wildly uneven thresholds (100 000 / 500 000 /
 * 2 000 000 credits), and a value-proportional bar squeezes the first
 * milestones into the left edge. Inside the active segment the fill still
 * moves proportionally, so the bar lands exactly on a marker the moment its
 * threshold is met.
 */
export const getStageProgress = (
  stages: Stage[],
  currentAmount: number
): number => {
  if (!stages.length) return 0;

  let previousValue = 0;
  for (let idx = 0; idx < stages.length; idx++) {
    const { value } = stages[idx];
    if (currentAmount < value) {
      const span = value - previousValue;
      const within = span > 0 ? (currentAmount - previousValue) / span : 0;
      const filled = idx + Math.min(Math.max(within, 0), 1);
      return (filled / stages.length) * 100;
    }
    previousValue = value;
  }
  return 100;
};

interface StageProgressProps {
  stages: Stage[];
  currentAmount: number;
  progress: number;
  currentStageId?: string;
}

interface LabelLayout {
  left: number;
  visible: boolean;
}

export default function StageProgress({
  stages,
  currentAmount,
  progress,
  currentStageId,
}: StageProgressProps) {
  const barRef = useRef<HTMLDivElement>(null);
  const labelRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const [layout, setLayout] = useState<LabelLayout[]>([]);

  // Identity of the rendered labels — the effect only needs to re-run when the
  // stages themselves change, not on every parent render (`stages` can be a
  // fresh array each time).
  const layoutKey = stages.map((stage) => `${stage.id}:${stage.award}`).join("|");

  // Labels are placed in px instead of with a `translateX(-50%)`: they have to
  // be clamped inside the bar (the last marker sits on its right edge) and the
  // ones that still collide after clamping are dropped, so a task with many
  // stages never renders numbers on top of each other.
  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return;

    const measure = () => {
      const barWidth = bar.getBoundingClientRect().width;
      if (!barWidth) return;

      const placed = stages.map((stage, idx) => {
        const width = labelRefs.current[idx]?.getBoundingClientRect().width ?? 0;
        const center = ((idx + 1) / stages.length) * barWidth;
        const left = Math.min(Math.max(center - width / 2, 0), barWidth - width);
        return { idx, left, right: left + width, id: stage.id };
      });

      // The stage the user is heading to matters most, then the final reward;
      // everything else fills whatever room is left, from the start onwards.
      const byPriority = [...placed].sort((a, b) => {
        const rank = (item: (typeof placed)[number]) =>
          item.id === currentStageId
            ? 0
            : item.idx === stages.length - 1
            ? 1
            : 2;
        return rank(a) - rank(b) || a.idx - b.idx;
      });

      const kept: typeof placed = [];
      byPriority.forEach((item) => {
        const collides = kept.some(
          (other) =>
            item.left < other.right + LABEL_GAP &&
            other.left < item.right + LABEL_GAP
        );
        if (!collides) kept.push(item);
      });

      const next = placed.map((item) => ({
        left: item.left,
        visible: kept.includes(item),
      }));

      setLayout((prev) =>
        prev.length === next.length &&
        prev.every(
          (item, idx) =>
            item.left === next[idx].left && item.visible === next[idx].visible
        )
          ? prev
          : next
      );
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutKey, currentStageId]);

  return (
    <div ref={barRef} className="relative h-8 mb-[14px] flex items-center">
      <div className="absolute w-full h-2 bg-white/08 rounded-full overflow-hidden">
        <div
          className="h-full bg-orange-100 rounded-full transition-[width] duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Stage markers */}
      {stages.map((stage, idx) => {
        const isReached = stage.value <= currentAmount;
        const isCurrent = stage.id === currentStageId;
        const isLast = idx === stages.length - 1;
        const placement = layout[idx];

        return (
          <Fragment key={stage.id}>
            <span
              ref={(node) => {
                labelRefs.current[idx] = node;
              }}
              className={cn(
                "absolute bottom-[26px] flex items-center gap-0.5 whitespace-nowrap text-xs font-semibold leading-130",
                isReached ? "text-orange-100" : "text-gray-1",
                { "text-white": isCurrent && !isReached }
              )}
              style={{
                // Before the first measurement the label is only rendered so
                // its width can be read; the effect then pins it down.
                left: placement?.left ?? 0,
                opacity: placement?.visible ? 1 : 0,
              }}
            >
              <DonIcon className="size-3 shrink-0" />
              {formatNumber(stage.award)}
            </span>
            <div
              className={cn("absolute bottom-[12px] w-0.5 h-2.5 rounded-full", {
                "bg-orange-100": isReached,
                "bg-gray-1": !isReached,
              })}
              style={{
                left: `${((idx + 1) / stages.length) * 100}%`,
                transform: isLast ? "translateX(-100%)" : "translateX(-50%)",
              }}
            />
          </Fragment>
        );
      })}
    </div>
  );
}
